// FTEDN Team - Community Moderation Page
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import './CommunityMod.css';

interface CommunityPost {
  id: string;
  authorName?: string;
  content?: string;
  pinned?: boolean;
  likes?: number;
}

const REMOVAL_REASON_MIN_LENGTH = 5;
const REMOVAL_REASON_ERROR = 'A reason (5+ chars) is required';

function getItems<T>(response: unknown): T[] {
  const payload = response as { data?: T[] };
  return Array.isArray(payload?.data) ? payload.data : [];
}

export function CommunityModPage() {
  const { get, patch, del } = useApi();
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState<string | null>(null);
  const [reasonDrafts, setReasonDrafts] = useState<Record<string, string>>({});
  const [reasonErrors, setReasonErrors] = useState<Record<string, string>>({});

  const loadPosts = useCallback(async () => {
    setError('');
    try {
      setPosts(getItems<CommunityPost>(await get('/community/posts')));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load community posts');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    void loadPosts();
  }, [loadPosts]);

  async function pinPost(post: CommunityPost) {
    setActingId(post.id);
    setError('');
    try {
      await patch(`/community/posts/${post.id}/pin`);
      await loadPosts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to pin community post');
    } finally {
      setActingId(null);
    }
  }

  async function removePost(post: CommunityPost) {
    const reason = (reasonDrafts[post.id] ?? '').trim();
    if (reason.length < REMOVAL_REASON_MIN_LENGTH) {
      setReasonErrors((current) => ({ ...current, [post.id]: REMOVAL_REASON_ERROR }));
      return;
    }

    setActingId(post.id);
    setError('');
    setReasonErrors((current) => ({ ...current, [post.id]: '' }));
    try {
      await del(`/community/posts/${post.id}`, { reason });
      setReasonDrafts((current) => ({ ...current, [post.id]: '' }));
      await loadPosts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove community post');
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="mgmt-page">
      <div className="mgmt-header">
        <h1>Community</h1>
        <p className="mgmt-subtitle">Moderate posts and make announcements</p>
        <div className="mgmt-stats">
          <span className="stat-chip">{posts.length} Posts</span>
          <span className="stat-chip approved">{posts.filter((post) => post.pinned).length} Pinned</span>
        </div>
      </div>
      {error && <div className="mgmt-error" role="alert">{error}</div>}
      <div className="community-mod-list">
        {loading && <div className="mgmt-empty">Loading community posts...</div>}
        {!loading && posts.length === 0 && <div className="mgmt-empty">No community posts yet.</div>}
        {posts.map((post) => {
          const author = post.authorName || 'Unknown creator';
          const reasonInputId = `community-removal-reason-${post.id}`;
          const reasonError = reasonErrors[post.id];
          return (
            <div key={post.id} className={`mod-row ${post.pinned ? 'pinned' : ''}`}>
              <div className="mod-row-info">
                <strong>{author}</strong>
                <span>{post.likes ?? 0} likes {post.pinned ? '- pinned' : ''}</span>
              </div>
              <p className="mod-content">{post.content || 'No post content'}</p>
              <div className="mgmt-status-reason">
                <label className="mgmt-reason-label" htmlFor={reasonInputId}>Removal reason</label>
                <input
                  id={reasonInputId}
                  className="mgmt-reason-input"
                  value={reasonDrafts[post.id] ?? ''}
                  onChange={(event) => {
                    setReasonDrafts((current) => ({ ...current, [post.id]: event.target.value }));
                    if (reasonError) {
                      setReasonErrors((current) => ({ ...current, [post.id]: '' }));
                    }
                  }}
                  placeholder="Removal reason"
                  aria-label={`Reason for ${author} community removal`}
                  aria-invalid={reasonError ? 'true' : 'false'}
                  aria-describedby={reasonError ? `${reasonInputId}-error` : undefined}
                />
                {reasonError && (
                  <p id={`${reasonInputId}-error`} className="mgmt-action-error" role="alert">
                    {reasonError}
                  </p>
                )}
              </div>
              <div className="mgmt-actions">
                <button className="action-btn view" disabled={actingId === post.id} onClick={() => void pinPost(post)} aria-label={`${post.pinned ? 'Unpin' : 'Pin'} post by ${author}`}>
                  {post.pinned ? 'Unpin' : 'Pin'}
                </button>
                <button className="action-btn reject" disabled={actingId === post.id} onClick={() => void removePost(post)} aria-label={`Remove post by ${author}`}>
                  Remove
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
